import {
  addDays,
  addHours,
  addMinutes,
  addMonths,
  addWeeks,
  addYears,
  differenceInDays,
  differenceInMilliseconds as fns_differenceInMilliseconds,
  differenceInMinutes as fns_differenceInMinutes,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isAfter as fns_isAfter,
  isBefore as fns_isBefore,
  isWithinInterval,
  Locale,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import fns_tz_format from 'date-fns-tz/format'
import fns_tz_toDate from 'date-fns-tz/toDate'
import { computed, makeObservable } from 'mobx'

import {
  ILocationClosureInterval,
  IWorkingHours,
  WorkflowDurationType,
} from '~/client/graph'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import InitialState from '~/client/src/shared/stores/InitialState'
import IMsDateInterval from '~/client/src/shared/types/IMsDateInterval'
import { capitalizeEachWord } from '~/client/src/shared/utils/capitalizeText'
import Guard from '~/client/src/shared/utils/Guard'
import {
  isWeekdayNumber,
  withResolvedOperatingMode,
} from '~/client/src/shared/utils/operatingIntervalMode'
import {
  EMPTY_DATE_VALUE,
  EMPTY_STRING,
  NO_VALUE,
} from '~/client/src/shared/utils/usefulStrings'

import DateTimeFormat from '../../enums/DateTimeFormat'
import {
  DEFAULT_WORK_FINISH_HOUR,
  DEFAULT_WORK_START_HOUR,
} from '../../models/Project'
import {
  DateTimeDisplayFormat,
  InternationalDateTimeDisplayFormat,
} from '../../utils/DateTimeFormats'
import {
  getRepresentativeWorkingHours,
  getScheduleWorkingDays,
} from '../../utils/projectSchedule'

export type DurationUnit =
  | 'years'
  | 'months'
  | 'weeks'
  | 'days'
  | 'hours'
  | 'minutes'
  | 'seconds'

// it is service patterns
// projectDateStore.get...ToDisplay should be used for display on UI
enum Format {
  SlashedDate = 'yyyy/MM/dd',
  ISODate = 'yyyy-MM-dd',
  Year = 'yyyy',
  YearAndMonth = 'yyyy-MM',
  WeekDayNum = 'e',
  HoursNum = 'H',
  DayOfMonth = 'd',
  ISOTime = 'HH:mm',
  ISODateTime = 'yyyy-MM-dd HH:mm:ss',
  ISOFullTime = 'HH:mm:ss',
  Timezone = 'xxx',
}

export const DAYS_IN_WEEK: number = 7
export const DAYS_IN_MONTH: number = 30
export const DEFAULT_ISO_DATE_STRING = '1970-01-01'
export const HOURS_IN_DAY = 24
export const MINUTES_IN_HOUR = 60
export const MINUTES_IN_DAY = MINUTES_IN_HOUR * 24
export const MINUTES_IN_WEEK = MINUTES_IN_DAY * DAYS_IN_WEEK
export const MINUTES_IN_MONTH = MINUTES_IN_DAY * DAYS_IN_MONTH

const MILLISECONDS_IN_SECOND = 1000
export const MILLISECONDS_IN_MINUTE = 60 * MILLISECONDS_IN_SECOND
const SECONDS_IN_HOUR = 3600
export const MILLISECONDS_IN_HOUR = MINUTES_IN_HOUR * MILLISECONDS_IN_MINUTE
export const MILLISECONDS_IN_DAY = HOURS_IN_DAY * MILLISECONDS_IN_HOUR
export const MILLISECONDS_IN_WEEK = DAYS_IN_WEEK * MILLISECONDS_IN_DAY
export const MILLISECONDS_IN_MONTH = DAYS_IN_MONTH * MILLISECONDS_IN_DAY

const getPronoun = (
  daysDiff: number,
  withComma?: boolean,
  withoutTomorrow?: boolean,
) => {
  let pronoun: string
  switch (daysDiff) {
    case -1:
      pronoun = Localization.translator.yesterday
      break
    case 0:
      pronoun = Localization.translator.today
      break
    case 1:
      if (withoutTomorrow) {
        return ''
      }
      pronoun = Localization.translator.tomorrow
      break
    default:
      return ''
  }

  return pronoun + (withComma ? ',' : '')
}

export const START_OF_DAY = '00:00'
const END_DAY_TIME = ' 23:59:59.999'

const intlOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  hour12: false,
}

const MAX_SAFE_AVAILABLE_TIMESTAMP = 32472136800000

/** Why an interval is not entirely inside worked time. */
export enum NonWorkTimeReason {
  /** The start or the finish lands on a date with no worked time at all. */
  EndpointDay = 'endpoint-day',
  /** An endpoint's time of day falls outside its own date's worked spans. */
  EndpointHours = 'endpoint-hours',
  /** A date between the endpoints carries no worked time. */
  MiddleDay = 'middle-day',
  /** A closed stretch inside a single date, between the two endpoints. */
  MiddleHours = 'middle-hours',
}

// STX1-1138: a workflow's dates are judged at their two endpoints only, not
// across the whole span — the interval-wide warnings introduced with STX1-983
// read as confusing in 0.234.0. The middle-half rule below is kept whole behind
// this switch rather than deleted, so flipping it back to true restores it.
// Exported so the specs covering that half can skip in step with it.
export const WHOLE_INTERVAL_NON_WORK_VALIDATION_ENABLED = false

export default class ProjectDateStore {
  public static createStoreForTimezone(timezoneId?: string) {
    return new ProjectDateStore({
      activeProject: { timezoneId },
    } as InitialState)
  }

  @computed
  public get weekdayNamesList(): string[] {
    const weekDays = this.getWeekDays(new Date())
    return weekDays.map(day => this.getWeekdayToDisplay(day))
  }

  public get monthsNamesList(): string[] {
    return Array.from({ length: 12 }, (e, i) => {
      return new Date(null, i + 1, null).toLocaleDateString('en', {
        month: 'short',
      })
    })
  }

  @computed
  public get weekdaysShortNamesList(): string[] {
    const weekDays = this.getWeekDays(new Date())
    return weekDays.map(day => this.getShortWeekdayToDisplay(day))
  }

  public readonly userTimezone =
    Intl.DateTimeFormat().resolvedOptions().timeZone

  private readonly datesCache: { [key: string]: Date } = {}
  private readonly dateStringsCache: { [key: string]: string } = {}

  public readonly usFormat = new DateTimeDisplayFormat()
  public readonly internationalFormat = new InternationalDateTimeDisplayFormat()

  public constructor(public state: InitialState) {
    makeObservable(this)
    Guard.requireAll({ state })
  }

  public getDateFormat(dateTimeFormat: DateTimeFormat): DateTimeDisplayFormat {
    return dateTimeFormat === DateTimeFormat.International
      ? this.internationalFormat
      : this.usFormat
  }

  public get currentFormat(): DateTimeDisplayFormat {
    const { dateTimeFormat } = this.state.activeProject
    if (dateTimeFormat === DateTimeFormat.International) {
      return this.internationalFormat
    }

    return this.usFormat
  }

  public format = (
    date: Date | number,
    pattern?: string,
    locale?: Locale,
    timezoneId?: string,
  ) => {
    return this.getDateInTimezone(
      date,
      timezoneId || this.timezoneId,
      pattern,
      locale,
    )
  }

  public getTimeToDisplay = (date: Date | number) => {
    return capitalizeEachWord(
      this.format(date, this.currentFormat.time, Localization.dateLocale),
    )
  }

  public getWeekdayToDisplay = (date: Date | number) => {
    return capitalizeEachWord(
      this.format(date, this.currentFormat.weekday, Localization.dateLocale),
    )
  }

  public getShortWeekdayToDisplay = (date: Date | number) => {
    return capitalizeEachWord(
      this.format(
        date,
        this.currentFormat.weekdayShort,
        Localization.dateLocale,
      ),
    )
  }

  public getMonthToDisplay = (date: Date | number) => {
    return capitalizeEachWord(
      this.format(date, this.currentFormat.month, Localization.dateLocale),
    )
  }

  public getYearToDisplay = (date: Date | number) => {
    return capitalizeEachWord(
      this.format(date, this.currentFormat.year, Localization.dateLocale),
    )
  }

  public getDayOfMonthToDisplay = (date: Date | number) => {
    return capitalizeEachWord(
      this.format(date, this.currentFormat.dayOfMonth, Localization.dateLocale),
    )
  }

  public getMonthAndDayToDisplay = (date: Date | number) => {
    return capitalizeEachWord(
      this.format(
        date,
        this.currentFormat.monthAndDay,
        Localization.dateLocale,
      ),
    )
  }

  public getSmartTimestamp = (date: Date | number): string => {
    if (this.isToday(date)) {
      return this.getTimeToDisplay(date)
    } else if (!this.isThisYear(date)) {
      return this.getMonthDayAndYearToDisplay(date).replace(/\b0(\d)/g, '$1')
    } else {
      return this.getMonthAndDayToDisplay(date).replace(/\b0(\d)/g, '$1')
    }
  }

  public getWeekdayMonthAndDayToDisplay = (date: Date | number) => {
    return capitalizeEachWord(
      this.format(
        date,
        this.currentFormat.weekdayMonthAndDay,
        Localization.dateLocale,
      ),
    )
  }

  public getMonthDayAndYearToDisplay = (date: Date | number) => {
    return capitalizeEachWord(
      this.format(
        date,
        this.currentFormat.monthDayAndYear,
        Localization.dateLocale,
      ),
    )
  }

  public getSlashedMonthDayAndYear = (date: Date | number): string => {
    return this.format(
      date,
      this.currentFormat.slashedMonthDayAndYear,
      Localization.dateLocale,
    )
  }

  public getMonthAndYearToDisplay = (date: Date | number) => {
    return capitalizeEachWord(
      this.format(
        date,
        this.currentFormat.monthAndYear,
        Localization.dateLocale,
      ),
    )
  }

  public getWeekdayMonthDayAndYearToDisplay = (date: Date | number) => {
    return capitalizeEachWord(
      this.format(
        date,
        this.currentFormat.weekdayMonthDayAndYear,
        Localization.dateLocale,
      ),
    )
  }

  public getMonthDayAndTimeToDisplay = (date: Date | number) => {
    return capitalizeEachWord(
      this.format(
        date,
        this.currentFormat.monthDayAndTime,
        Localization.dateLocale,
      ),
    )
  }

  public getMonthDayYearAndTimeToDisplay = (date: Date | number) => {
    return capitalizeEachWord(
      this.format(
        date,
        this.currentFormat.monthDayYearAndTime,
        Localization.dateLocale,
      ),
    )
  }

  public getWeekdayMonthDayAndTimeToDisplay = (date: Date | number) => {
    return capitalizeEachWord(
      this.format(
        date,
        this.currentFormat.weekdayMonthDayAndTime,
        Localization.dateLocale,
      ),
    )
  }

  public getWeekdayMonthDayYearAndTimeToDisplay = (date: Date | number) => {
    return capitalizeEachWord(
      this.format(
        date,
        this.currentFormat.weekdayMonthDayYearAndTime,
        Localization.dateLocale,
      ),
    )
  }

  // Tue, Mar 24, 2020 12:00 AM - 12:15 AM when same day
  // Tue, Mar 24, 2020 12:00 AM - 12:15 AM (+{countOfDay}d) when diff days
  // (-) - Tue, Mar 24, 2020 12:00 AM - when startMs omitted
  // Tue, Mar 24, 2020 12:00 AM - (-) - when startMs omitted
  public getTimeIntervalToDisplay = (
    startMs: number,
    endMs: number,
    baseDateFormatFn?: (date: Date | number) => string,
  ) => {
    const formatFn =
      baseDateFormatFn || this.getWeekdayMonthDayYearAndTimeToDisplay

    let endValue = endMs ? this.getTimeToDisplay(endMs) : EMPTY_DATE_VALUE
    if (endMs && !this.isSameDay(startMs, endMs)) {
      if (startMs) {
        const daysDiff = this.countDaysToDate(startMs, endMs)
        endValue = `${endValue} (+${daysDiff}${Localization.translator.d_daysShort})`
      } else {
        endValue = formatFn(endMs)
      }
    }

    return `${startMs ? formatFn(startMs) : EMPTY_DATE_VALUE} - ${endValue}`
  }

  // May 26, 06:00 AM – Jun 30, 06:00 PM
  public getMonthDayAndTimeIntervalPerDayToDisplay = (
    startDate: Date | number,
    endDate: Date | number,
  ) => {
    return (
      this.getMonthDayAndTimeToDisplay(startDate) +
      ' – ' +
      this.getMonthDayAndTimeToDisplay(endDate)
    )
  }

  // 12:00 AM - 13:00 AM
  public getTimeIntervalPerDayToDisplay = (
    startDate: Date | number,
    endDate: Date | number,
  ) => {
    return (
      this.getTimeToDisplay(startDate) + ' – ' + this.getTimeToDisplay(endDate)
    )
  }

  // Feb 21, 10–11 AM | Feb 21, 10:30–11 AM | Feb 20, 6 AM – 4:30 PM
  public getCompactDateTimeInterval = (
    startDate: Date | number,
    endDate: Date | number,
  ): string => {
    const datePart = this.getMonthAndDayToDisplay(startDate).replace(
      /\b0(\d)/g,
      '$1',
    )
    const [startTime, startAmPm] = this.getShortTime(startDate)
    const [endTime, endAmPm] = this.getShortTime(endDate)

    const startSuffix = startAmPm.toUpperCase()
    const endSuffix = endAmPm.toUpperCase()

    let timePart: string
    if (startSuffix === endSuffix) {
      timePart = `${startTime}–${endTime} ${endSuffix}`
    } else {
      timePart = `${startTime} ${startSuffix} – ${endTime} ${endSuffix}`
    }

    if (!this.isSameDay(startDate, endDate)) {
      const endDatePart = this.getMonthAndDayToDisplay(endDate).replace(
        /\b0(\d)/g,
        '$1',
      )
      return `${datePart}, ${startTime} ${startSuffix} – ${endDatePart}, ${endTime} ${endSuffix}`
    }

    return `${datePart}, ${timePart}`
  }

  // 11am-5pm, 6-8am, 3-8pm, 4-5:30pm
  public getShortTimeIntervalPerDay = (
    startDate: Date | number,
    endDate: Date | number,
  ): string => {
    const [startTime, startAmOrPm] = this.getShortTime(startDate)
    const [endTime, endAmOrPm] = this.getShortTime(endDate)

    const formattedAmOrPm =
      startAmOrPm === endAmOrPm ? EMPTY_STRING : startAmOrPm

    return `${startTime}${formattedAmOrPm}–${endTime}${endAmOrPm}`
  }
  public getDateYearIntervalToDisplay = (
    startDate: Date | number,
    endDate: Date | number,
  ) => {
    if (!this.isSameYear(startDate, endDate)) {
      return this.getDateIntervalToDisplay(startDate, endDate)
    }

    return (
      this.getDateIntervalToDisplay(startDate, endDate) +
      ', ' +
      this.getYearToDisplay(endDate)
    )
  }

  public getDateIntervalToDisplay = (
    startDate: Date | number,
    endDate: Date | number,
  ) => {
    if (this.isSameDay(startDate, endDate)) {
      return this.getMonthAndDayToDisplay(startDate)
    }

    if (!this.isSameYear(startDate, endDate)) {
      return (
        this.getYearToDisplay(startDate) +
        ' - ' +
        this.getMonthDayAndYearToDisplay(endDate)
      )
    }

    const {
      intervalSameMonthStartDate,
      intervalSameMonthEndDate,
      monthAndDay,
    } = this.currentFormat

    const isOneMonthInterval = this.isSameMonth(startDate, endDate)
    const startDateFormat = isOneMonthInterval
      ? intervalSameMonthStartDate
      : monthAndDay
    const endDateFormat = isOneMonthInterval
      ? intervalSameMonthEndDate
      : monthAndDay
    return capitalizeEachWord(
      this.format(startDate, startDateFormat, Localization.dateLocale) +
        ' - ' +
        this.format(endDate, endDateFormat, Localization.dateLocale),
    )
  }

  public getFullDateIntervalToDisplay = (
    startDate: Date | number,
    endDate: Date | number,
  ) => {
    const { weekdayMonthDayAndYear } = this.currentFormat

    return capitalizeEachWord(
      this.format(startDate, weekdayMonthDayAndYear, Localization.dateLocale) +
        ' – ' +
        this.format(endDate, weekdayMonthDayAndYear, Localization.dateLocale),
    )
  }

  // 2020-02-22
  public getDashedFormattedDate = (date: Date | number) => {
    return this.format(date, Format.ISODate)
  }

  // 22:33
  public getISOTime = (date: Date | number) => {
    return this.format(date, Format.ISOTime)
  }

  // Today, Sun, Feb 23, 2020 or Today, Sun 23 Feb 2020
  public getPronounDateString = (date: Date, withoutTomorrow?: boolean) => {
    const pronoun = getPronoun(
      this.countDaysToDate(new Date(), date),
      true,
      withoutTomorrow,
    )
    return pronoun + ' ' + this.getWeekdayMonthDayAndYearToDisplay(date)
  }

  public getPronounWithMonthDayAndTimeString = (
    date: Date | number,
    withoutTomorrow?: boolean,
  ) => {
    const pronoun = getPronoun(
      this.countDaysToDate(new Date(), date),
      false,
      withoutTomorrow,
    )

    return pronoun
      ? pronoun + ', ' + this.getTimeToDisplay(date)
      : this.getMonthDayAndTimeToDisplay(date)
  }

  public getPronounOrMonthAndDayString = (
    date: Date | number,
    withoutTomorrow?: boolean,
  ) => {
    const pronoun = getPronoun(
      this.countDaysToDate(new Date(), date),
      false,
      withoutTomorrow,
    )

    return pronoun || this.getMonthAndDayToDisplay(date)
  }

  // Feb 20 or 20 Feb or -
  public getMonthAndDayToDisplayWithCheck = (date: Date | number) => {
    return this.isValidDate(date) ? this.getMonthAndDayToDisplay(date) : ' - '
  }

  // yesterday or today or tomorrow
  public getPronoun = (date: Date | number = new Date()) => {
    return getPronoun(this.countDaysToDate(new Date(), date))
  }

  // 22 -> 10:00 PM
  public getFullHourLabel = (hour: number, minutes = 0) => {
    // no convertation is needed, it is just about format number as time
    const date = new Date()
    date.setHours(hour, minutes)
    return format(date, this.currentFormat.time, {
      locale: Localization.dateLocale,
    })
  }

  public isWithinDateInterval = (
    startDate: Date | number,
    endDate: Date | number,
    date: Date | number,
  ) => {
    return isBetween(
      this.startOfDay(startDate),
      this.endOfDay(endDate),
      this.startOfDay(date).getTime() + 1, // if startDate === date method should return true
    )
  }

  public isWithinTimeInterval = (
    startDate: number,
    endDate: number,
    dateToCheck: Date | number,
  ): boolean => {
    const dateToCheckTime = this.getTimeMs(dateToCheck)
    const startTime = this.getTimeMs(startDate)
    const endTime = this.getTimeMs(endDate)

    return startTime > endTime
      ? !isWithinRange(dateToCheckTime, endTime, startTime)
      : isWithinRange(dateToCheckTime, startTime, endTime)
  }

  public isToday = (date: Date | number) => {
    return this.isSameDay(date, new Date())
  }

  public isTomorrow = (date: Date | number) => {
    return this.isToday(this.addDays(date, -1))
  }

  public isBeforeToday = (date: Date | number) => {
    return isBefore(new Date(date), this.startOfDay(new Date()))
  }

  public areDatesEqualToMinutes = (
    date1: Date | number,
    date2: Date | number,
  ): boolean => {
    return (
      this.format(date1, Format.ISODate) ===
        this.format(date2, Format.ISODate) &&
      this.format(date1, Format.ISOTime) === this.format(date2, Format.ISOTime)
    )
  }

  public isSameDay = (date1: Date | number, date2: Date | number) => {
    return (
      this.format(date1, Format.ISODate) === this.format(date2, Format.ISODate)
    )
  }

  public isSameWeek = (date1: Date | number, date2: Date | number) => {
    return this.isSameDay(this.startOfWeek(date1), this.startOfWeek(date2))
  }

  public isSameMonth = (date1: Date | number, date2: Date | number) => {
    return (
      this.format(date1, Format.YearAndMonth) ===
      this.format(date2, Format.YearAndMonth)
    )
  }

  public isSameYear = (date1: Date | number, date2: Date | number) => {
    return this.format(date1, Format.Year) === this.format(date2, Format.Year)
  }

  public isThisYear = (date: Date | number) => {
    return this.isSameYear(date, new Date())
  }

  public isSunday = (date: Date | number) => {
    return this.format(date, Format.WeekDayNum) === '1'
  }

  public isSaturday = (date: Date | number) => {
    return this.format(date, Format.WeekDayNum) === '7'
  }

  public isWeekend = (date: Date | number) => {
    const day = this.format(date, Format.WeekDayNum)
    return day === '1' || day === '7'
  }

  public getWeekdayNumber = (date: Date | number) => {
    return this.format(date, Format.WeekDayNum)
  }

  public isLeap = (year: number) => {
    return new Date(year, 1, 29).getDate() === 29
  }

  public isClosedBetween = (
    startDate: Date,
    endDate: Date,
    operatingIntervals: IMsDateInterval[],
    closedIntervals: Array<IMsDateInterval | ILocationClosureInterval>,
    type: 'partially' | 'fully' = 'fully',
  ) => {
    // Date-aware: a DAILY window is instantiated on every day the range spans, while a
    // DATED window keeps its own absolute dates and supersedes the daily one on the
    // days it covers. Previously every window was stamped onto the range's start day
    // by time-of-day alone, so a dated window leaked onto unrelated days and widened
    // them instead of narrowing them.
    const operatingHours = this.buildConcreteOperatingWindows(
      startDate,
      endDate,
      operatingIntervals,
    )

    const isIntervalWithinOperationHours = doIntervalsContainInterval(
      operatingHours,
      startDate,
      endDate,
    )
    const isOpenedDuringDay =
      !operatingHours.length || isIntervalWithinOperationHours.fully
    const isClosed = {
      partially: !isOpenedDuringDay,
      fully: !isOpenedDuringDay && !isIntervalWithinOperationHours.partially,
    }

    return (
      doIntervalsContainInterval(closedIntervals, startDate, endDate)[type] ||
      isClosed[type] ||
      closedIntervals.some(interval =>
        this.isDailyIntervalIntersectsWithDates(
          interval as ILocationClosureInterval,
          startDate,
          endDate,
        ),
      )
    )
  }

  // Whether [startDate, endDate] is fully covered by the operating windows —
  // expanding the DAILY window onto every calendar day the interval spans (plus the
  // day before, so an overnight window covers an early-morning tail) and keeping
  // each DATED window on its own absolute dates. Unlike isClosedBetween, which
  // anchors every window to the start day only, this is multi-day aware. Each
  // window's `isDaily` must be set by the caller.
  //
  // A DATED window OVERRIDES the daily one on every day it touches: those days
  // allow only what the dated windows say, so a resource can open outside — or
  // close within — its usual hours on specific dates. Days no dated window touches
  // fall back to the daily window.
  //
  // The override is applied to the daily instances' COVERED time, not to the day
  // each one is anchored on: an overnight instance is anchored on one day and runs
  // into the next, so anchor-day bookkeeping would both drop coverage from days no
  // dated window touches and leak coverage onto days a dated window owns. Each
  // instance is therefore split at midnight and only the pieces on non-dated days
  // are kept.
  public isIntervalWithinOperatingWindows = (
    startDate: Date | number,
    endDate: Date | number,
    windows: IMsDateInterval[],
  ): boolean => {
    const concrete = this.buildConcreteOperatingWindows(
      startDate,
      endDate,
      windows,
    )
    return doIntervalsContainInterval(
      concrete,
      new Date(startDate),
      new Date(endDate),
    ).fully
  }

  // The concrete windows an operating-hours set allows across [startDate, endDate],
  // per the rules documented on isIntervalWithinOperatingWindows. Each window's mode
  // is resolved here rather than trusted from the caller: an unresolved legacy
  // interval would otherwise fall into the dated branch and be pinned to its stored
  // date, closing the resource on every other day.
  public buildConcreteOperatingWindows = (
    startDate: Date | number,
    endDate: Date | number,
    rawWindows: IMsDateInterval[],
  ): IMsDateInterval[] => {
    const windows = rawWindows.map(withResolvedOperatingMode)
    const start = new Date(startDate)
    const end = new Date(endDate)
    const concrete: IMsDateInterval[] = []

    // Days and hours are the PROJECT's throughout: a window belongs to the site, so
    // "the days a dated window covers" and "what time a daily window opens" are both
    // questions about the project's calendar, not the viewer's. Reading them on the
    // browser's clock would let a dated override land on the wrong project day for an
    // off-tz viewer — suppressing a day's daily hours that the override never touched.
    // Short-circuits to plain date-fns when the two zones agree.
    const startOfProjectDay = this.startOfDay

    // Only days in [start - 1 day, end] can ever be consulted (see the daily
    // expansion below), so the dated-day set is clamped to that span — a dated
    // window spanning years must not cost a loop iteration per day of its own
    // length on a validator that runs inside picker renders.
    const firstConsultedDay = this.addDays(startOfProjectDay(start), -1)
    const lastConsultedDay = startOfProjectDay(end)

    // Start-of-day keys (same project buckets as the daily expansion below) of every
    // day a dated window touches.
    const datedDays = new Set<number>()

    for (const window of windows) {
      if (window.isDaily) continue

      // Dated window: keep its own absolute range so it can't bleed onto another
      // day.
      const windowStart = new Date(window.startDate)
      const windowEnd = new Date(window.endDate ?? window.startDate)
      concrete.push({
        startDate: windowStart.getTime(),
        endDate: windowEnd.getTime(),
      })

      let cursor = new Date(
        Math.max(
          startOfProjectDay(windowStart).getTime(),
          firstConsultedDay.getTime(),
        ),
      )
      // End-exclusive: a window ending exactly at midnight (Aug 5 00:00 -> Aug 6
      // 00:00) covers only Aug 5, so it must not claim Aug 6 and suppress that day's
      // daily hours — it contributes no coverage there to replace them. Stepping back
      // a millisecond leaves every range that ends mid-day on the same days.
      const lastDatedDay = Math.min(
        startOfProjectDay(windowEnd.getTime() - 1).getTime(),
        lastConsultedDay.getTime(),
      )
      while (cursor.getTime() <= lastDatedDay) {
        datedDays.add(cursor.getTime())
        cursor = this.addDays(cursor, 1)
      }
    }

    // Second pass: datedDays must be complete before any daily window is expanded,
    // so this cannot be folded into the loop above.
    for (const window of windows) {
      if (!window.isDaily) continue

      // Daily window: instantiate it on every day from the day before `start`
      // through `end`; the day-before covers an overnight window's morning tail.
      // The window carries only a time of day, read in project time — hoisted out of
      // the loop because resolving it is a timezone conversion, not a field read.
      const [startHours, startMinutes] = this.getHoursMinutes(window.startDate)
      const [endHours, endMinutes] = this.getHoursMinutes(
        window.endDate ?? window.startDate,
      )
      let cursor = new Date(firstConsultedDay)

      while (cursor.getTime() <= lastConsultedDay.getTime()) {
        // A weekday-scoped window is instantiated only on its own weekday. Its
        // overnight tail still reaches the next day through the wrap below, which
        // is the only way that day may be covered: instantiating it there in its
        // own right would let a Thursday window admit a Friday booking.
        if (
          isWeekdayNumber(window.dayOfWeek) &&
          Number(this.getWeekdayNumber(cursor)) !== window.dayOfWeek
        ) {
          cursor = this.addDays(cursor, 1)
          continue
        }

        // A dated window replaces the SHIFT anchored on its day, not the clock hours
        // of the calendar day. An overnight instance is one shift: overridden, it
        // does not happen, so its tail into the next morning does not either; left
        // alone, it runs to its end even though a dated window governs the day it
        // finishes in. Splitting it at midnight instead let a Tuesday daytime
        // override quietly cut Monday night short.
        if (datedDays.has(cursor.getTime())) {
          cursor = this.addDays(cursor, 1)
          continue
        }

        const dayStart = this.setHours(cursor, startHours, startMinutes)
        let dayEnd = this.setHours(cursor, endHours, endMinutes)
        // An overnight window (end at or before start) wraps into the next day.
        if (dayEnd.getTime() <= dayStart.getTime()) {
          dayEnd = this.addDays(dayEnd, 1)
        }
        concrete.push({
          startDate: dayStart.getTime(),
          endDate: dayEnd.getTime(),
        })
        cursor = this.addDays(cursor, 1)
      }
    }

    return concrete
  }

  public startOfDay = (date: Date | number) => {
    if (!this.isProjectTimezoneDifferent) {
      return startOfDay(date)
    }

    const dateString = this.format(date, Format.ISODate)
    return this.fromIsoString(dateString)
  }

  public endOfDay = (date: Date | number) => {
    if (!this.isProjectTimezoneDifferent) {
      return endOfDay(date)
    }

    const dateString = this.format(date, Format.ISODate) + END_DAY_TIME
    return this.fromIsoString(dateString)
  }

  public startOfWeek = (date: Date | number) => {
    if (!this.isProjectTimezoneDifferent) {
      return startOfWeek(date)
    }

    const weekDay = Number(this.format(date, Format.WeekDayNum))
    const startWeekDate = this.addDays(date, -weekDay + 1)
    const isoDateString = this.format(startWeekDate, Format.ISODate)

    return this.fromIsoString(isoDateString)
  }

  public endOfWeek = (date: Date | number) => {
    if (!this.isProjectTimezoneDifferent) {
      return endOfWeek(date)
    }

    const weekDay = Number(this.format(date, Format.WeekDayNum))
    const endWeekDate = this.addDays(date, DAYS_IN_WEEK - weekDay)
    const isoDateString =
      this.format(endWeekDate, Format.ISODate) + END_DAY_TIME

    return this.fromIsoString(isoDateString)
  }

  public getWeekRange = (date: number | Date) => {
    if (isNaN(Number(new Date(date)))) return

    const firstDayOfWeek = this.startOfWeek(date)
    const endDayOfWeek = this.endOfWeek(date)

    return [
      this.getMonthDayAndYearToDisplay(firstDayOfWeek),
      this.getMonthDayAndYearToDisplay(endDayOfWeek),
    ].join(NO_VALUE)
  }

  public getMonthRange = (date: number | Date) => {
    if (isNaN(Number(new Date(date)))) return

    const firstDayOfMonth = this.startOfMonth(date)
    const endDayOfMonth = this.endOfMonth(date)

    return [
      this.getMonthDayAndYearToDisplay(firstDayOfMonth),
      this.getMonthDayAndYearToDisplay(endDayOfMonth),
    ].join(NO_VALUE)
  }

  public getWeekDays = (date: Date | number) => {
    const sunday = this.startOfWeek(date)
    const weekDays = [sunday]
    for (let i = 1; i < 7; i++) {
      weekDays.push(this.addDays(sunday, i))
    }
    return weekDays
  }

  public startOfMonth = (date: Date | number) => {
    if (!this.isProjectTimezoneDifferent) {
      return startOfMonth(date)
    }

    const dateString = this.format(date, Format.YearAndMonth)
    return this.fromIsoString(dateString + '-01')
  }

  public getMinAvailableDate = () => {
    return this.startOfDay(-MAX_SAFE_AVAILABLE_TIMESTAMP)
  }

  public getMaxAvailableDate = () => {
    return this.startOfDay(MAX_SAFE_AVAILABLE_TIMESTAMP)
  }

  public endOfMonth = (date: Date | number) => {
    if (!this.isProjectTimezoneDifferent) {
      return endOfMonth(date)
    }

    const [year, month] = this.getYearMonthDay(date)
    const localDate = new Date().setFullYear(year, month, 0)
    return this.fromIsoString(format(localDate, Format.ISODate) + END_DAY_TIME)
  }

  public getDayOfMonth = (date: Date | number) => {
    return Number(this.format(date, Format.DayOfMonth))
  }

  public getFilteredDaysCount = (
    from: Date,
    to: Date,
    filterPredicate: (date: Date) => boolean,
    shouldExcludeStartDay?: boolean,
  ) => {
    const intervalDays = eachDayOfInterval({
      start: this.convertFromProjectDate(from),
      end: this.convertFromProjectDate(to),
    }).map(date => this.convertToProjectDate(date))
    const { length } = intervalDays.filter(filterPredicate)

    return shouldExcludeStartDay ? length - 1 : length
  }

  public differenceInCalendarMonths = (
    dateLeft: Date | number,
    dateRight: Date | number,
  ) => {
    const [yearL, monthL] = this.getYearMonthDay(dateLeft)
    const [yearR, monthR] = this.getYearMonthDay(dateRight)
    return (yearL - yearR) * 12 + (monthL - monthR)
  }

  public getDaysInMonth = (date: Date | number) => {
    const [year, month] = this.getYearMonthDay(date)
    const lastDayOfMonth = new Date()
    lastDayOfMonth.setFullYear(year, month, 0)
    lastDayOfMonth.setHours(0, 0, 0, 0)
    return lastDayOfMonth.getDate()
  }

  public getDaysInYear = (date: Date | number) => {
    const [year] = this.getYearMonthDay(date)
    return this.isLeap(year) ? 366 : 365
  }

  public getHoursMinutes = (date: Date | number) => {
    return this.getISOTime(date)
      .split(':')
      .map(el => Number(el))
  }

  // 13:30 -> [13, 30]
  public getHoursMinutesFromISOTime = (isoTime: string) => {
    return isoTime.split(':').map(Number)
  }

  public getDaySeconds = (date: Date | number) => {
    const [hours, minutes] = this.getHoursMinutes(date)
    return (
      (minutes * MILLISECONDS_IN_MINUTE + hours * MILLISECONDS_IN_HOUR) /
      MILLISECONDS_IN_SECOND
    )
  }

  public getHours = (date: Date | number) => {
    return Number(this.format(date, Format.HoursNum))
  }

  public getTimeMs = (date: Date | number) => {
    const dateBeginingMs = this.startOfDay(date).getTime()
    const dateMs = new Date(date).getTime()
    return Math.max(dateMs - dateBeginingMs, 0)
  }

  public setTimeMs = (date: Date | number, timeMs: number) => {
    const dayBeginingMs = this.startOfDay(date).getTime()
    return new Date(dayBeginingMs + timeMs)
  }

  // (base | today) - date
  public countDaysToDate = (
    date: Date | number,
    base: Date | number = null,
    shouldAccountBaseDate?: boolean,
  ): number => {
    const target = this.startOfDay(new Date(base || Date.now()))
    const otherDate = this.startOfDay(date)

    const daysCount = differenceInDays(target, otherDate)
    return shouldAccountBaseDate ? daysCount + 1 : daysCount
  }

  public addMonths = (date: Date | number, amount: number) => {
    if (!amount) {
      return new Date(date)
    }

    if (!this.isProjectTimezoneDifferent) {
      return addMonths(date, amount)
    }

    const [year, month, day] = this.getYearMonthDay(date)
    const targetDate = new Date(year, month - 1 + amount, 1)
    const daysInMonth = new Date(
      targetDate.getFullYear(),
      targetDate.getMonth() + 1,
      0,
    ).getDate()
    targetDate.setDate(Math.min(day, daysInMonth))

    return this.fromIsoString(
      format(targetDate, Format.ISODate) +
        ' ' +
        this.format(date, Format.ISOFullTime),
    )
  }

  public addYears = (
    date: Date | number,
    amount: number,
    shouldIgnoreTimezone?: boolean,
  ) => {
    if (!amount) {
      return new Date(date)
    }

    if (!this.isProjectTimezoneDifferent || shouldIgnoreTimezone) {
      return addYears(date, amount)
    }

    const [year, month, day] = this.getYearMonthDay(date)
    const localDate = new Date().setFullYear(year + amount, month - 1, day)
    return this.fromIsoString(
      format(localDate, Format.ISODate) +
        ' ' +
        this.format(date, Format.ISOFullTime),
    )
  }

  public addWorkingDays = (date: Date, amount: number): Date => {
    if (!this.hasWorkingDays || !amount) {
      return this.addDays(date, amount)
    }

    let workingDate = date

    for (let counter = 0; amount < 0 ? counter > amount : counter < amount;) {
      workingDate = this.addDays(workingDate, amount < 0 ? -1 : 1)

      if (this.isWorkingDay(workingDate)) {
        amount < 0 ? counter-- : counter++
      }
    }

    return workingDate
  }

  public addDays = (date: Date | number, amount: number) => {
    if (!amount) {
      return new Date(date)
    }

    if (!this.isProjectTimezoneDifferent) {
      return addDays(date, amount)
    }

    const [year, month, day] = this.getYearMonthDay(date)
    const localDate = new Date().setFullYear(year, month - 1, day + amount)
    return this.fromIsoString(
      format(localDate, Format.ISODate) +
        ' ' +
        this.format(date, Format.ISOFullTime),
    )
  }

  public addHours = (date: Date | number, amount: number) => {
    if (!amount) {
      return new Date(date)
    }

    return addHours(date, amount)
  }

  public addMinutes = (date: Date | number, amount: number) => {
    if (!amount) {
      return new Date(date)
    }

    return addMinutes(date, amount)
  }

  public addWeeks = (date: Date | number, amount: number) => {
    if (!amount) {
      return new Date(date)
    }

    if (!this.isProjectTimezoneDifferent) {
      return addWeeks(date, amount)
    }

    const localDate = addWeeks(
      new Date(this.format(date, Format.SlashedDate)),
      amount,
    )
    const newDateStr =
      format(localDate, Format.ISODate) +
      ' ' +
      this.format(date, Format.ISOFullTime)
    return this.fromIsoString(newDateStr)
  }

  public getDaysInInterval = (
    start: Date | number,
    end: Date | number,
  ): Date[] => {
    let startDate = new Date(start)
    const endDate = new Date(end)
    if (isAfter(startDate, endDate)) {
      return []
    }

    const dates = []
    while (!this.isSameDay(startDate, endDate)) {
      dates.push(startDate)
      startDate = this.startOfDay(this.addDays(startDate, 1))
    }

    dates.push(startDate)
    return dates
  }

  public getDaysCountInInterval = (
    start: Date | number,
    end: Date | number,
  ): number => {
    const startDate = new Date(start)
    const endDate = new Date(end)

    if (isAfter(startDate, endDate)) {
      return 0
    }

    // Calculate the difference in milliseconds
    const timeDiff = endDate.getTime() - startDate.getTime()

    // Convert to days and round up to include both start and end days
    const daysDiff = Math.ceil(timeDiff / MILLISECONDS_IN_DAY) + 1

    return daysDiff
  }

  public setHours = (date: Date, hours: number, minutes: number) => {
    const isoDate = this.format(date, Format.ISODate)
    const isoTime = padWithZero(hours) + ':' + padWithZero(minutes) + ':00'
    return this.combineISODateTime(isoDate, isoTime)
  }

  public convertToProjectDate = (date: Date | number) => {
    if (this.isProjectTimezoneDifferent) {
      const dateString = format(date, Format.ISODateTime)
      return this.fromIsoString(dateString)
    }

    return new Date(date)
  }

  public convertFromProjectDate = (date: Date | number) => {
    if (this.isProjectTimezoneDifferent) {
      const dateString = this.format(date, Format.ISODateTime)
      return parseISOCustom(dateString)
    }

    return new Date(date)
  }

  public combineISODateTime = (
    ISODate: string = DEFAULT_ISO_DATE_STRING,
    ISOTime: string = START_OF_DAY,
  ): Date => {
    return this.fromIsoString(ISODate + ' ' + ISOTime)
  }

  // This wrapper is needed to ensure that we are always working with a copy of the cached date returning from "_fromIsoString"
  // Otherwise the date object may have been modified somewhere and this will lead to inconsistencies in "datesCache"
  public fromIsoString = (dateISO: string) => {
    if (!dateISO) {
      return null
    }

    return new Date(this._fromIsoString(dateISO))
  }

  private _fromIsoString = (dateISO: string) => {
    if (!dateISO) {
      return null
    }

    const localDate = parseISOCustom(dateISO)

    if (this.isProjectTimezoneDifferent) {
      const cacheKey = dateISO + this.timezoneId
      const cachedValue = this.datesCache[cacheKey]
      if (cachedValue) {
        return cachedValue
      }

      const convertedDate = fns_tz_toDate(dateISO, {
        timeZone: this.timezoneId,
      })

      const convertedHours = this.getHours(convertedDate)
      let dstDiff =
        convertedHours -
        this.getHours(convertedDate.getTime() - MILLISECONDS_IN_HOUR) -
        1
      if (dstDiff < 0) {
        dstDiff += HOURS_IN_DAY
      }
      const expectedHours = localDate.getHours() + dstDiff
      if (convertedHours !== expectedHours) {
        const discrepancy =
          (expectedHours - convertedHours) * MILLISECONDS_IN_HOUR
        return (this.datesCache[cacheKey] = new Date(
          convertedDate.getTime() + discrepancy,
        ))
      }

      return (this.datesCache[cacheKey] = convertedDate)
    }

    return localDate
  }

  public getEndDate = ({
    startDate,
    daysToAdd,
  }: {
    startDate: Date
    daysToAdd: number
  }) => {
    if (!daysToAdd) {
      return this.endOfDay(startDate)
    }

    return this.endOfDay(this.addDays(startDate, daysToAdd))
  }

  public get timezoneId() {
    return this.state.activeProject.timezoneId
  }

  public getClientTimezoneId = () => {
    return this.timezoneId || this.userTimezone
  }

  public getClientTimezoneOffsetAsString = () => {
    return 'GMT ' + this.format(new Date(), Format.Timezone)
  }

  public isValidDate = (date: Date | number | string) => {
    if (!date && date !== 0) {
      return false
    }

    if (date.constructor === String) {
      return isFinite(parseISOCustom(date as string) as any)
    }

    return (
      date.constructor === Number ||
      (date.constructor === Date && isFinite(date as any))
    )
  }

  public get workingDaysMap(): { [key: string]: boolean } {
    return this.state.activeProject.projectWorkingDaysMap
  }

  public get hasWorkingDays(): boolean {
    return !!getScheduleWorkingDays(this.state.activeProject.workingSchedule)
      .length
  }

  /**
   * One window standing for the whole week: the range the most days share.
   *
   * Derived from `workingSchedule` rather than read off the legacy
   * `workingHours`, which is on its way out — the server computes that field the
   * same way, so this is the same number by a route that survives its removal.
   * Correct only where something genuinely wants a week-shaped answer, such as a
   * calendar axis shared across days or the default for a day whose row carries
   * no hours; anything asking about a date wants `getWorkingHoursForDate`
   * (STX1-983).
   */
  public get projectWorkingHours(): IWorkingHours {
    const workingHours = getRepresentativeWorkingHours(
      this.state.activeProject.workingSchedule,
      this.getDaySeconds,
    )

    const [defaultStartHours, defaultStartMinutes] = workingHours?.startTime
      ? this.getHoursMinutes(workingHours.startTime)
      : [DEFAULT_WORK_START_HOUR, 0]

    const [defaultEndHours, defaultEndMinutes] = workingHours?.endTime
      ? this.getHoursMinutes(workingHours.endTime)
      : [DEFAULT_WORK_FINISH_HOUR, 0]

    const now = new Date()

    const startTime = this.setHours(now, defaultStartHours, defaultStartMinutes)
    const endTime = this.setHours(now, defaultEndHours, defaultEndMinutes)

    return { startTime, endTime }
  }

  /**
   * The window worked on a given date (STX1-983). A weekday listed in the
   * project's `workingSchedule` carries its own hours; anything else falls back
   * to the project's single window — a project saved before per-day hours
   * existed, or a row missing one of its times.
   *
   * Anchored to `date` rather than today, so the returned instants belong to the
   * day being asked about.
   */
  /** The weekdays the project works, ascending. 1 = Sunday .. 7 = Saturday. */
  public get workingWeekdayNumbers(): number[] {
    return getScheduleWorkingDays(this.state.activeProject.workingSchedule)
  }

  /**
   * The window a weekday works. Same lookup as `getWorkingHoursForDate` without
   * needing a date to ask about, for callers describing the week itself rather
   * than a particular day of it.
   */
  public getWorkingHoursForWeekday = (dayOfWeek: number): IWorkingHours => {
    const day = this.state.activeProject.workingSchedule?.find(
      d => d?.dayOfWeek === dayOfWeek,
    )

    if (day?.startTime == null || day?.endTime == null) {
      return this.projectWorkingHours
    }

    return { startTime: day.startTime, endTime: day.endTime } as IWorkingHours
  }

  public getWorkingHoursForDate = (date: Date | number): IWorkingHours => {
    const dayNumber = Number(this.getWeekdayNumber(date))
    const day = this.state.activeProject.workingSchedule?.find(
      d => d?.dayOfWeek === dayNumber,
    )

    if (day?.startTime == null || day?.endTime == null) {
      return this.projectWorkingHours
    }

    const [startHours, startMinutes] = this.getHoursMinutes(day.startTime)
    const [endHours, endMinutes] = this.getHoursMinutes(day.endTime)

    return {
      startTime: this.setHours(new Date(date), startHours, startMinutes),
      endTime: this.setHours(new Date(date), endHours, endMinutes),
    } as IWorkingHours
  }

  /** Minutes from midnight of a date's working window, in project time. */
  /**
   * The single interval "All day" fills on `date`.
   *
   * A day that is worked keeps its own window, finishing on the following date
   * when it runs past midnight — the shift, not the calendar day. A day nobody
   * selected that the previous day's window reaches into gets that reach
   * instead: midnight through to where the previous day stops. Anything else has
   * no worked time to fill, and keeps the day's configured window so the caller
   * still has something to seed; the interval check is what reports the day.
   *
   * Both instants are anchored to `date`, so callers can use them directly.
   */
  public getAllDayWindowForDate = (date: Date | number): IWorkingHours => {
    const ownWindow = (): IWorkingHours => {
      const { startTime, endTime } = this.getWorkingHoursForDate(date)
      const [startMinute, endMinute] = this.getWorkingMinutesForDate(date)

      return {
        startTime: this.atTimeOfDay(date, startTime),
        endTime:
          endMinute <= startMinute
            ? this.addDays(this.atTimeOfDay(date, endTime), 1)
            : this.atTimeOfDay(date, endTime),
      } as IWorkingHours
    }

    if (this.isWorkingWeekday(date) || this.isHolidayOrProjectClosure(date)) {
      return ownWindow()
    }

    const spillMinutes = this.getSpillMinutesIntoDate(date)
    if (!spillMinutes) {
      return ownWindow()
    }

    const startTime = this.startOfDay(date)

    return {
      startTime,
      endTime: this.addMinutes(startTime, spillMinutes),
    } as IWorkingHours
  }

  /** `timeOfDay`'s hour and minute, moved onto `date`. */
  private atTimeOfDay = (
    date: Date | number,
    timeOfDay: Date | number,
  ): Date => {
    const [hours, minutes] = this.getHoursMinutes(timeOfDay)

    return this.setHours(new Date(date), hours, minutes)
  }

  private getWorkingMinutesForDate = (date: Date | number): number[] => {
    const { startTime, endTime } = this.getWorkingHoursForDate(date)
    const [startHours, startMinutes] = this.getHoursMinutes(startTime)
    const [endHours, endMinutes] = this.getHoursMinutes(endTime)

    return [
      startHours * MINUTES_IN_HOUR + startMinutes,
      endHours * MINUTES_IN_HOUR + endMinutes,
    ]
  }

  private isWorkingWeekday = (date: Date | number): boolean => {
    return !!this.workingDaysMap[this.getWeekdayNumber(date)]
  }

  /**
   * The minute-of-day spans worked on a calendar date, ordered and disjoint.
   *
   * A window whose start is after its end runs overnight, so it covers
   * `[start, midnight)` on its own weekday and `[midnight, end]` on the next.
   * That second half is why a day that is not itself a working day can still
   * carry worked hours: Monday 22:00–03:00 means Monday 22:00–24:00 and Tuesday
   * 00:00–03:00, whether or not Tuesday is worked. A window whose start equals
   * its end is a 24-hour day and spills nowhere.
   *
   * Holidays and closures suppress the day entirely, including a window that
   * would otherwise spill into it.
   */
  public getWorkingMinuteSpansForDate = (date: Date | number): number[][] => {
    if (this.isHolidayOrProjectClosure(date)) {
      return []
    }

    const spans: number[][] = []

    if (this.isWorkingWeekday(date)) {
      const [start, end] = this.getWorkingMinutesForDate(date)

      // Ends after it starts and the window closes the same day; otherwise it
      // runs to midnight and the rest lands on the next date. Ends that coincide
      // are the 24-hour day, which is that same shape — a window starting at
      // 06:00 works to midnight here and until 06:00 tomorrow, not from midnight
      // today.
      spans.push(start < end ? [start, end] : [start, MINUTES_IN_DAY])
    }

    const spillMinutes = this.getSpillMinutesIntoDate(date)
    if (spillMinutes) {
      spans.push([0, spillMinutes])
    }

    return spans.sort(([a], [b]) => a - b)
  }

  /**
   * The minutes of the day at which this date's work starts or stops.
   *
   * Spans that touch are one stretch of work — where an overnight window runs
   * into the next day's own hours nobody starts or stops — and the midnight cuts
   * at either end of the date belong to the calendar rather than to the
   * schedule. So neither is a boundary, and the 24-hour day has none (STX1-983).
   */
  public getWorkingBoundaryMinutesForDate = (date: Date | number): number[] => {
    const merged: number[][] = []

    // The spans arrive sorted by start, so a span either extends the one before
    // it or opens a new stretch.
    this.getWorkingMinuteSpansForDate(date).forEach(([start, end]) => {
      const previous = merged[merged.length - 1]

      if (previous && start <= previous[1]) {
        previous[1] = Math.max(previous[1], end)
        return
      }

      merged.push([start, end])
    })

    const boundaries = merged
      .flat()
      .filter(minute => minute > 0 && minute < MINUTES_IN_DAY)

    return Array.from(new Set(boundaries)).sort((a, b) => a - b)
  }

  public get isProjectStartsLaterThanEnds(): boolean {
    return this.projectWorkingHours.startTime > this.projectWorkingHours.endTime
  }

  public get projectStartHourMinutes(): number[] {
    return this.getHoursMinutes(this.projectWorkingHours.startTime)
  }

  public get projectEndHourMinutes(): number[] {
    return this.getHoursMinutes(this.projectWorkingHours.endTime)
  }

  /**
   * How long a workflow may run and still be one working day: the length of the
   * interval "All day" fills on `date`, so the cap and the checkbox cannot mean
   * two different things about the same day.
   *
   * Elapsed minutes between the window's own instants rather than a difference
   * of clock times — the workflow it is compared against is measured the same
   * way, and a day carrying a daylight-saving shift is not the length its clock
   * claims.
   */
  public getWorkingDayDurationInMinutes = (date: Date | number): number => {
    const { startTime, endTime } = this.getAllDayWindowForDate(date)

    return fns_differenceInMinutes(endTime, startTime)
  }

  public getDifferenceInMinutes = (
    startDate: Date | number,
    endDate: Date | number,
    isFullDayCount = true,
  ): number => {
    const [startHours, startMinutes] = this.getHoursMinutes(startDate)
    const [endHours, endMinutes] = this.getHoursMinutes(endDate)

    const startInMinutes = startHours * MINUTES_IN_HOUR + startMinutes
    const endInMinutes = endHours * MINUTES_IN_HOUR + endMinutes

    let difference = endInMinutes - startInMinutes
    if (difference < 0 || (difference === 0 && isFullDayCount)) {
      difference += HOURS_IN_DAY * MINUTES_IN_HOUR
    }

    return difference
  }

  public isHolidayOrProjectClosure = (date: Date | number): boolean => {
    return this.state.activeProject.closedIntervals?.some(i =>
      isWithinRange(
        this.startOfDay(date),
        this.startOfDay(i.startDate),
        this.endOfDay(i.endDate),
      ),
    )
  }

  public isWorkingDay = (date: Date | number): boolean => {
    const dayNumber = this.getWeekdayNumber(date)
    return (
      !this.isHolidayOrProjectClosure(date) && !!this.workingDaysMap[dayNumber]
    )
  }

  /** Whether a date carries any worked time — its own window, or a spill. */
  /**
   * How far into `date` the previous day's window reaches, in minutes — 0 when
   * it does not reach at all. A window ending at or before it starts runs past
   * midnight; one ending exactly at midnight has nothing left to give.
   */
  private getSpillMinutesIntoDate = (date: Date | number): number => {
    const previousDate = this.addDays(date, -1)
    if (
      !this.isWorkingWeekday(previousDate) ||
      this.isHolidayOrProjectClosure(previousDate)
    ) {
      return 0
    }

    const [previousStart, previousEnd] =
      this.getWorkingMinutesForDate(previousDate)

    return previousEnd <= previousStart ? previousEnd : 0
  }

  public hasWorkingHours = (date: Date | number): boolean => {
    return !!date && !!this.getWorkingMinuteSpansForDate(date).length
  }

  /**
   * Whether an instant falls inside one of its own date's worked spans, so a
   * time is judged against the day it actually lands on. 01:00 on a Tuesday
   * that is not itself worked is inside working hours when Monday runs
   * 22:00–03:00 (STX1-983).
   */
  public isTimeInsideWorkingHours = (date: Date | number): boolean => {
    // Callers are form fields that may still be empty, and reading a time off
    // nothing throws rather than returning a verdict.
    if (!date) {
      return false
    }

    const [hours, minutes] = this.getHoursMinutes(date)
    const minuteOfDay = hours * MINUTES_IN_HOUR + minutes

    return this.getWorkingMinuteSpansForDate(date).some(
      ([spanStart, spanEnd]) =>
        minuteOfDay >= spanStart && minuteOfDay <= spanEnd,
    )
  }

  /**
   * Both ends of the interval sit in worked time.
   *
   * Endpoints rather than full coverage, which is the long-standing contract
   * here: an interval spanning several days is expected to pass on its start
   * and finish times, not to be rejected for containing the nights in between.
   */
  public areTimesInsideWorkingHours = (
    startDate: Date | number,
    endDate: Date | number,
  ): boolean => {
    return (
      this.isTimeInsideWorkingHours(startDate) &&
      this.isTimeInsideWorkingHours(endDate)
    )
  }

  /**
   * No separate `isWorkingDay` test: a date that is not worked has no spans, so
   * the hours check already rejects it. Asking the weekday as well would reject
   * 01:00 on an unchecked Tuesday that Monday's 22:00–03:00 window reaches into
   * — worked time the calendar now draws and the server accepts (STX1-983).
   */
  public isInsideWorkingDaysAndHours = (
    startDate: Date | number,
    endDate: Date | number,
  ): boolean => {
    return this.areTimesInsideWorkingHours(startDate, endDate)
  }

  /** Minutes from midnight of an instant, in project time. */
  private getMinuteOfDay = (date: Date | number): number => {
    const [hours, minutes] = this.getHoursMinutes(date)
    return hours * MINUTES_IN_HOUR + minutes
  }

  /**
   * Whether one date's worked spans cover a minute-of-day range without a
   * break. Spans arrive sorted and disjoint, so the first one that starts later
   * than the point already reached is a gap.
   */

  /**
   * Everything wrong with an interval, not just the first thing found.
   *
   * The endpoints are judged as they always were — each against its own date's
   * spans. What this adds is the middle: every date between them must carry
   * worked time, so an unworked day, a holiday or a closure inside the interval
   * is caught even when both ends are fine.
   *
   * All of it is reported together because the problems are independent: a start
   * time outside its window says nothing about a holiday three days later, and
   * returning only the first would hide the second from the form.
   *
   * The nights at the day boundaries stay tolerated, because a multi-day
   * interval contains them by definition — a Monday-to-Wednesday job is not
   * invalid for spanning two nights. Contiguous coverage is therefore required
   * only within a single date, where a closed midday gap is the one unworked
   * stretch that is not a day boundary.
   */
  public getNonWorkTimeReasons = (
    startDate: Date | number,
    endDate: Date | number,
  ): NonWorkTimeReason[] => {
    if (!startDate || !endDate) {
      return []
    }

    const reasons: NonWorkTimeReason[] = []

    // A day with no worked time at all reports the day, not the clock, so an
    // endpoint is never blamed twice for the same fault.
    const isStartDayWorked = this.hasWorkingHours(startDate)
    const isEndDayWorked = this.hasWorkingHours(endDate)
    if (!isStartDayWorked || !isEndDayWorked) {
      reasons.push(NonWorkTimeReason.EndpointDay)
    }

    const hasEndpointHoursFault =
      (isStartDayWorked && !this.isTimeInsideWorkingHours(startDate)) ||
      (isEndDayWorked && !this.isTimeInsideWorkingHours(endDate))
    if (hasEndpointHoursFault) {
      reasons.push(NonWorkTimeReason.EndpointHours)
    }

    if (!WHOLE_INTERVAL_NON_WORK_VALIDATION_ENABLED) return reasons

    // A single instant, or a reversed pair: there is no middle, and ordering is
    // somebody else's validation.
    if (new Date(endDate).getTime() <= new Date(startDate).getTime()) {
      return reasons
    }

    const endMinute = this.getMinuteOfDay(endDate)
    const isOneDay = this.isSameDay(startDate, endDate)

    // Every minute the interval spans has to be worked, night included: an
    // interval reaching across one is asking for time the site is shut, and the
    // interval carries no way to say otherwise. A date nothing is worked on is
    // reported as a day instead, so a holiday is not also described as hours.
    let hasUnworkedTime =
      isStartDayWorked &&
      !this.isRangeFullyWorked(
        startDate,
        this.getMinuteOfDay(startDate),
        isOneDay ? endMinute : MINUTES_IN_DAY,
      )

    if (!isOneDay) {
      const lastDayMs = this.startOfDay(endDate).getTime()
      let cursor = this.startOfDay(this.addDays(startDate, 1))
      let hasUnworkedDay = false

      while (
        cursor.getTime() < lastDayMs &&
        cursor.getTime() <= this.getScheduleScanLimitMs(startDate)
      ) {
        // The walk carries on past an unworked day rather than stopping at it,
        // since a later date can leave hours unworked on its own account and
        // that fault outlives fixing this one. The day itself is not measured
        // for hours — it is already reported as a day.
        if (this.hasWorkingHours(cursor)) {
          hasUnworkedTime =
            hasUnworkedTime ||
            !this.isRangeFullyWorked(cursor, 0, MINUTES_IN_DAY)
        } else {
          hasUnworkedDay = true
        }

        cursor = this.startOfDay(this.addDays(cursor, 1))
      }

      if (hasUnworkedDay) {
        reasons.push(NonWorkTimeReason.MiddleDay)
      }

      hasUnworkedTime =
        hasUnworkedTime ||
        (isEndDayWorked && !this.isRangeFullyWorked(endDate, 0, endMinute))
    }

    // Reported alongside whatever else was found rather than instead of it: the
    // walk skips an unworked day rather than measuring it, so these are the
    // minutes *other* dates leave unworked — a fault that survives fixing the
    // day. Where a wording is shared, the message layer collapses it.
    if (hasUnworkedTime) {
      reasons.push(NonWorkTimeReason.MiddleHours)
    }

    return reasons
  }

  /**
   * How far the walk over an interval's days has to go before the schedule
   * stops holding surprises. Past the last closure the week simply repeats, so
   * seven consecutive dates show every pattern the schedule can produce and a
   * fault beyond them has already been seen. Closures are the only thing that
   * breaks that periodicity, hence walking as far as the last of them.
   */
  private getScheduleScanLimitMs = (startDate: Date | number): number => {
    const closures = this.state.activeProject.closedIntervals
    const lastClosureMs = closures?.length
      ? Math.max(...closures.map(({ endDate }) => Number(endDate)))
      : 0

    const settledFrom = Math.max(
      this.startOfDay(startDate).getTime(),
      lastClosureMs,
    )

    return this.addDays(settledFrom, DAYS_IN_WEEK).getTime()
  }

  /**
   * Whether every minute of `date` between the two given ones is worked. The
   * spans are walked in order and merged as they go, so windows that touch or
   * overlap count as one stretch and the first real break ends it.
   */
  private isRangeFullyWorked = (
    date: Date | number,
    fromMinute: number,
    toMinute: number,
  ): boolean => {
    let reachedMinute = fromMinute

    for (const [spanStart, spanEnd] of this.getWorkingMinuteSpansForDate(
      date,
    )) {
      if (spanStart > reachedMinute) {
        break
      }
      reachedMinute = Math.max(reachedMinute, spanEnd)

      if (reachedMinute >= toMinute) {
        return true
      }
    }

    return reachedMinute >= toMinute
  }

  /** The whole interval sits in worked time — see `getNonWorkTimeReasons`. */
  public isIntervalInsideWorkingHours = (
    startDate: Date | number,
    endDate: Date | number,
  ): boolean => {
    return !this.getNonWorkTimeReasons(startDate, endDate).length
  }

  public getYearMonthDay(date: Date | number) {
    const dateString = this.format(date, Format.ISODate)
    return dateString.split('-').map(v => Number(v))
  }

  public isDailyIntervalIntersectsWithDates = (
    interval: ILocationClosureInterval,
    checkStartDate: Date | number,
    checkEndDate: Date | number,
  ): boolean => {
    if (!interval.isDaily) {
      return false
    }

    const startDateTime = this.getTimeMs(checkStartDate)
    const endDateTime = this.getTimeMs(checkEndDate)
    const intervalStartTime = this.getTimeMs(interval.startDate)
    const intervalEndTime = this.getTimeMs(interval.endDate)

    if (intervalStartTime > intervalEndTime && startDateTime > endDateTime) {
      return true
    }

    if (startDateTime > endDateTime || intervalStartTime > intervalEndTime) {
      return this.areRevertedTimesIntersect(
        startDateTime,
        endDateTime,
        intervalStartTime,
        intervalEndTime,
      )
    }

    return areIntervalTimesIntersects(
      {
        startDate: new Date(startDateTime),
        endDate: new Date(endDateTime),
      },
      {
        startDate: new Date(intervalStartTime),
        endDate: new Date(intervalEndTime),
      },
    )
  }

  public get isProjectTimezoneDifferent() {
    return this.timezoneId && this.timezoneId !== this.userTimezone
  }

  private getDateInTimezone(
    date: Date | number,
    timeZone: string,
    formatting?: string,
    locale?: Locale,
  ) {
    if (!this.isValidDate(date)) {
      return null
    }

    const datePattern = formatting || this.currentFormat.monthDayYearAndTime
    if (timeZone && timeZone !== this.userTimezone) {
      const cacheKey =
        timeZone + datePattern + locale + new Date(date).getTime()
      if (this.dateStringsCache[cacheKey]) {
        return this.dateStringsCache[cacheKey]
      }
      // dateFnsTz.format converts time incorrectly
      // so use browser api directly with a hack
      // omg in chrome 80 00:00 is converted to 24:00
      const shiftedDate = new Date(
        new Intl.DateTimeFormat(
          'en-US',
          Object.assign({ timeZone }, intlOptions) as unknown, // Trick to avoid type error
        )
          .format(new Date(date))
          .replace(' 24:', ' 00:'),
      )
      return (this.dateStringsCache[cacheKey] = fns_tz_format(
        shiftedDate,
        datePattern,
        { timeZone, locale },
      ))
    }

    return format(new Date(date), datePattern, { locale })
  }

  private getShortTime = (date: Date | number): string[] => {
    const formattedDate = this.format(
      date,
      this.currentFormat.shortTime,
      Localization.dateLocale,
    ).toLowerCase()

    const [hours, minutesWithAmOrPm] = formattedDate.split(':')
    const [minutes, amOrPm = EMPTY_STRING] = minutesWithAmOrPm.split(' ')

    const time = `${hours}${minutes === '00' ? EMPTY_STRING : `:${minutes}`}`

    return [time, amOrPm]
  }

  private areRevertedTimesIntersect = (
    startLeftTime: number,
    endLeftTime: number,
    startRightTime: number,
    endRightTime: number,
  ): boolean => {
    const areLeftTimesReverted = startLeftTime > endLeftTime

    const startATime = areLeftTimesReverted ? startLeftTime : startRightTime
    const endATime = this.addDays(
      areLeftTimesReverted ? endLeftTime : endRightTime,
      1,
    )
    const startBTime = areLeftTimesReverted ? startRightTime : startLeftTime
    const endBTime = areLeftTimesReverted ? endRightTime : endLeftTime

    return (
      areIntervalTimesIntersects(
        {
          startDate: new Date(startATime),
          endDate: endATime,
        },
        {
          startDate: new Date(startBTime),
          endDate: new Date(endBTime),
        },
      ) ||
      areIntervalTimesIntersects(
        {
          startDate: new Date(startATime),
          endDate: endATime,
        },
        {
          startDate: this.addDays(startBTime, 1),
          endDate: this.addDays(endBTime, 1),
        },
      )
    )
  }

  /**
   * The last date in `date`'s week that is worked, at the start of that day.
   *
   * Distinct from `getLastWorkingDay`, which answers when that day's shift
   * *finishes* — a different date whenever the shift runs past midnight. Callers
   * wanting a day to read a schedule off want this one.
   */
  public getLastWorkingDate = (date: Date | number): Date => {
    let lastWorkingDay = this.endOfWeek(date)
    const firstDayOfWeek = this.startOfWeek(date)

    while (
      !this.isWorkingDay(lastWorkingDay) &&
      !isBefore(lastWorkingDay, firstDayOfWeek)
    ) {
      lastWorkingDay = addDays(lastWorkingDay, -1)
    }

    return this.startOfDay(lastWorkingDay)
  }

  /** When the week's last shift finishes — see `getAllDayWindowForDate`. */
  public getLastWorkingDay = (date: Date | number): Date => {
    const { endTime } = this.getAllDayWindowForDate(
      this.getLastWorkingDate(date),
    )

    return new Date(endTime)
  }

  public validateMaxDuration = (
    startDate: Date | number,
    endDate: Date | number,
    maxDurationType: WorkflowDurationType,
    maxDuration: number,
  ): boolean => {
    if (!startDate || !endDate || !maxDurationType || maxDuration === null) {
      return false
    }

    if (maxDurationType === WorkflowDurationType.AllDay) {
      // One working day means the day the workflow starts on, so the limit is
      // what "All day" fills there — no same-day test, since an overnight day
      // legitimately puts its two ends on two dates.
      //
      // This deliberately parts company with the non-work-time check: work
      // continuing across two shifts is entirely worked time, and still more
      // than one day. An interval running from a spill into the day's own window
      // is accepted there and capped here, the same way a Monday-to-Wednesday
      // interval is (STX1-983).
      return (
        fns_differenceInMinutes(endDate, startDate) <=
        this.getWorkingDayDurationInMinutes(startDate)
      )
    }

    if (maxDurationType === WorkflowDurationType.WorkWeek) {
      // Measured against the end of the start day's own week, which is enough on
      // its own: an end past that fails here whichever later week it falls in.
      // Asking for the same calendar week as well used to refuse the week's own
      // last shift, because a Saturday running to 02:00 finishes on the Sunday
      // that starts the next one. Ordering is the picker's validation
      // (`isEndDateInvalid`), not the cap's (STX1-983).
      return isBefore(
        endDate,
        this.addMinutes(this.getLastWorkingDay(startDate), 1),
      )
    }

    return (
      !maxDuration ||
      isBefore(endDate, this.addMinutes(startDate, maxDuration + 1))
    )
  }
}
export const isAfter = fns_isAfter
export const isBefore = fns_isBefore
export const differenceInMinutes = fns_differenceInMinutes
export const differenceInMilliseconds = fns_differenceInMilliseconds

export const isWithinRange = (
  dateToCheck: Date | number,
  start: Date | number,
  end: Date | number,
) => {
  return isWithinInterval(dateToCheck, { start, end })
}

export function isBetween(
  startDate: Date | number,
  endDate: Date | number,
  dateToCheck: Date | number,
) {
  return isBefore(dateToCheck, endDate) && isAfter(dateToCheck, startDate)
}

export function padWithZero(value: number): string {
  return String(value).padStart(2, '0')
}

export function isLateDay(currentDate: Date, dueDate: Date) {
  return currentDate.getTime() - dueDate.getTime() > 0
}

export function areIntervalTimesIntersects(
  {
    startDate: startADate,
    endDate: endADate,
  }: { startDate: Date; endDate: Date },
  {
    startDate: startBDate,
    endDate: endBDate,
  }: { startDate: Date; endDate: Date },
) {
  const startATime = startADate.getTime()
  const endATime = endADate.getTime()
  const startBTime = startBDate.getTime()
  const endBTime = endBDate.getTime()

  return endATime > startBTime && startATime < endBTime
}

// 259200 s. -> 72 h.
export function secondsToFullHours(seconds: number): number {
  return Math.floor(seconds / SECONDS_IN_HOUR)
}

// 100900000 -> 28:02 (not limited by 24 hours)
export function millisecondsToTime(ms: number): string {
  const minutes = String(Math.ceil((ms / MILLISECONDS_IN_MINUTE) % 60))
  const hours = String(Math.floor(ms / MILLISECONDS_IN_HOUR))

  return [hours, minutes].map(t => padWithZero(+t)).join(':')
}

/**
 * Cross-browser parsing ISO 8601 string to local date avoiding browser specific devilry
 * @param dateISO
 */
function parseISOCustom(dateISO: string): Date {
  // @ts-ignore: use array as tuple intentionally
  // year, monthIndex (from 0) [, day [, hours [, minutes [, seconds [, ms]]]]]
  return new Date(...dateISO.split(/\D/).map((d, i) => (i === 1 ? d - 1 : d)))
}

function doIntervalsContainInterval(
  intervals: IMsDateInterval[],
  checkStartDate: Date,
  checkEndDate: Date,
) {
  const startMs = new Date(checkStartDate).getTime()
  const endMs = new Date(checkEndDate).getTime()

  const inRangeIntervals = intervals
    .map(({ startDate, endDate }) => ({
      startDate: new Date(startDate).getTime(),
      endDate: endDate ? new Date(endDate).getTime() : endMs,
    }))
    .filter(
      ({ startDate, endDate }) =>
        startDate < endDate && startDate < endMs && endDate > startMs,
    )
    .sort((a, b) => a.startDate - b.startDate)

  const response = {
    partially: !!inRangeIntervals.length,
    fully: false,
  }

  let prevTimePosition = startMs
  for (const interval of inRangeIntervals) {
    if (interval.startDate > prevTimePosition) {
      return response
    }
    prevTimePosition = Math.max(prevTimePosition, interval.endDate)
    if (prevTimePosition >= endMs) {
      response.fully = true
      return response
    }
  }

  return response
}
